class KlaviyoForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });
  }

  get apiKey() {
    return this.getAttribute('api-key');
  }

  get listId() {
    return this.getAttribute('list-id');
  }

  get successMessage() {
    return this.getAttribute('success-message') || 'Thanks for subscribing!';
  }

  get errorMessage() {
    return this.getAttribute('error-message') || 'Something went wrong. Please try again.';
  }

  get bannerAlignment() {
    return this.getAttribute('banner-alignment') || 'center';
  }

  async handleSubmit() {
    const emailInput = this.form.querySelector('input[type="email"]');
    const email = emailInput?.value;
    if (!email) return;

    const submitButton = this.form.querySelector('button[type="submit"]');
    if (submitButton) submitButton.setAttribute('aria-busy', 'true');

    // Remove any existing banner
    const existingBanner = this.form.querySelector('.banner');
    if (existingBanner) existingBanner.remove();

    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.apiKey)}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/vnd.api+json',
          'revision': '2026-01-15'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Website Newsletter',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: 'SUBSCRIBED'
                        }
                      }
                    }
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: this.listId
                }
              }
            }
          }
        })
      });

      if (response.status === 202) {
        this.showBanner('success', this.successMessage);
        localStorage.setItem('theme:popup-filled', 'true');
        emailInput.value = '';
      } else {
        this.showBanner('error', this.errorMessage);
      }
    } catch {
      this.showBanner('error', this.errorMessage);
    } finally {
      if (submitButton) submitButton.removeAttribute('aria-busy');
    }
  }

  showBanner(status, message) {
    const banner = document.createElement('div');
    banner.className = `banner banner--${status} text-${this.bannerAlignment}`;
    banner.innerHTML = `<div class="banner__content">${message}</div>`;
    this.form.prepend(banner);
  }
}

if (!customElements.get('klaviyo-form')) {
  customElements.define('klaviyo-form', KlaviyoForm);
}

class GiftHintForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Clear any stale banner whenever the form becomes visible again
    // (modal reopened, mobile panel reopened, etc.)
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.clearBanner();
        }
      });
      observer.observe(this);
    }
  }

  get apiKey() {
    return this.getAttribute('api-key');
  }

  get productTitle() {
    return this.getAttribute('product-title');
  }

  get productUrl() {
    return this.getAttribute('product-url');
  }

  get productImage() {
    return this.getAttribute('product-image');
  }

  get productPrice() {
    return this.getAttribute('product-price');
  }

  async handleSubmit() {
    const data = new FormData(this.form);
    const senderName = data.get('sender_name')?.trim();
    const senderEmail = data.get('sender_email')?.trim();
    const recipientName = data.get('recipient_name')?.trim();
    const recipientEmail = data.get('recipient_email')?.trim();
    const message = data.get('message')?.trim() || '';

    if (!senderName || !senderEmail || !recipientName || !recipientEmail) return;

    const submitButton = this.form.querySelector('button[type="submit"]');
    if (submitButton) submitButton.setAttribute('aria-busy', 'true');

    this.clearBanner();

    const sharedProperties = {
      sender_name: senderName,
      sender_email: senderEmail,
      recipient_name: recipientName,
      recipient_email: recipientEmail,
      message,
      product_title: this.productTitle,
      product_url: this.productUrl,
      product_image: this.productImage,
      product_price: this.productPrice,
      discount_code: 'Barkly&five'
    };

    try {
      const [recipientResp, senderResp] = await Promise.all([
        // Event 1: profile = recipient. Triggers the "hint email" flow.
        this.sendEvent('Gift Hint Received', { email: recipientEmail, first_name: recipientName }, sharedProperties),
        // Event 2: profile = sender. Triggers the optional "thanks for sending" flow.
        this.sendEvent('Gift Hint Submitted', { email: senderEmail, first_name: senderName }, sharedProperties)
      ]);

      if (recipientResp.status === 202 && senderResp.status === 202) {
        this.showBanner('success', 'Your hint has been sent!');
        this.form.reset();
      } else {
        this.showBanner('error', 'Something went wrong. Please try again.');
      }
    } catch {
      this.showBanner('error', 'Something went wrong. Please try again.');
    } finally {
      if (submitButton) submitButton.removeAttribute('aria-busy');
    }
  }

  sendEvent(metricName, profileAttributes, properties) {
    return fetch(
      `https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(this.apiKey)}`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/vnd.api+json',
          'revision': '2026-01-15'
        },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: {
                data: { type: 'metric', attributes: { name: metricName } }
              },
              profile: {
                data: { type: 'profile', attributes: profileAttributes }
              },
              properties
            }
          }
        })
      }
    );
  }

  clearBanner() {
    this.querySelectorAll('.gift-hint-modal__banner').forEach(b => b.remove());
  }

  showBanner(status, message) {
    this.clearBanner();
    const banner = document.createElement('div');
    banner.className = `gift-hint-modal__banner gift-hint-modal__banner--${status}`;
    banner.textContent = message;
    this.form.prepend(banner);

    if (status === 'success') {
      clearTimeout(this._bannerTimer);
      this._bannerTimer = setTimeout(() => this.clearBanner(), 5000);
    }
  }
}

if (!customElements.get('gift-hint-form')) {
  customElements.define('gift-hint-form', GiftHintForm);
}

// On mobile, intercept the gift hint trigger and show the full-screen panel
// instead of the x-modal (capture phase runs before x-modal's delegated listener)
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('.gift-hint-trigger');
  if (!trigger || !window.matchMedia('(max-width: 749px)').matches) return;

  e.stopImmediatePropagation();

  const modalId = trigger.getAttribute('aria-controls');
  const panelId = modalId?.replace('gift-hint-modal-', 'gift-hint-panel-');
  const panel = panelId && document.getElementById(panelId);
  if (!panel) return;

  panel.removeAttribute('hidden');
  panel.classList.add('is-open');
  document.body.style.overflow = 'hidden';
}, true);

document.addEventListener('click', (e) => {
  const closeBtn = e.target.closest('.gift-hint-mobile-close');
  if (!closeBtn) return;

  const panel = closeBtn.closest('.gift-hint-mobile-panel');
  if (!panel) return;

  panel.classList.remove('is-open');
  document.body.style.overflow = '';
});
